import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Layout, Menu, notification, PageHeader, Popconfirm, Row, Space, Table, Tag, Tooltip } from "antd";
import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";

const accountUserBrandsQuery = gql`
  query ($pagination: ConnectionPagination, $sorting: AccountUserBrandSorting, $searchQuery: String) {
    accountUserBrands(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        user {
          id
          email
        }
        brand {
          id
          code
        }
        salesperson {
          id
          code
          email
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const deleteAccountUserBrandMutation = gql`
  mutation ($id: ID!) {
    deleteAccountUserBrand(id: $id) {
      userBrand {
        id
        user {
          id
          email
        }
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data, refetch } = useQuery(accountUserBrandsQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleNewClick = () => {
    navigate("new");
  };

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [execDelete] = useMutation(deleteAccountUserBrandMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this User Brand",
      });
    },
    onCompleted: ({ deleteAccountUserBrand: { userBrand } }) => {
      notification.success({
        placement: "bottomRight",
        message: `User Brand "${userBrand.user.email}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "userBrands.user",
      render: (value: string, resource: any) => <p>{resource.user.email}</p>,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "userBrands.brand",
      render: (value: string, resource: any) =>
        resource.brand.code === "palto" ? (
          <Tag>{resource.brand.code.toUpperCase()}</Tag>
        ) : (
          <Tag color="volcano">{resource.brand.code.toUpperCase()}</Tag>
        ),
    },
    {
      title: "Salesperson",
      dataIndex: "salesperson",
      render: (value: string, resource: any) => <p>{resource.salesperson?.code}</p>,
    },
    {
      width: "80px",
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
            />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="User Brands"
          extra={
            <Button onClick={handleNewClick} type="primary">
              <PlusOutlined />
              New
            </Button>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.accountUserBrands.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
